// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { combineActions, handleActions } from 'redux-actions';

import {
    CHECKOUT_ORDER_SUCCESS,
    DELETE_SELECTED_ACCESSORY,
    DELETE_SELECTED_PARENT,
    GET_PART_INFO_SUCCESS,
    PUT_PART_INFO_SUCCESS,
    GET_REPAIR_OFFER,
    GET_REGISTER_INFO_DATA,
    GET_REGISTER_INFO_DATA_SUCCESS,
    GET_JARVIS_DATA_SUCCESS,
    GET_REGISTRATION_SUCCESS,
    GET_SHIPPING_ADDRESS_DATA_SUCCESS,
    GET_SHIPPING_DEVICE_OFFER_DATA_SUCCESS,
    PURCHASE_ORDER_SUCCESS,
    SAVE_USER_DEVICE_AND_ACCESSORY_SELECTION,
    SAVE_ZIP_CODE_DATA,
    SAVE_SAFETY_FORM_DATA,
    SAVE_SELECTED_OFFER,
    RESET_SELECTED_OFFER,
    UPDATE_CREATE_ORDER_PROGRESS,
    SAVE_REPAIR_OFFER,
    RESET_ORDER_DATA,
    GET_SHIPPING_ACCESSORY_OFFER_DATA_SUCCESS,
    GET_SELECTED_SHIPPING_OFFER_DATA_SUCCESS,
    RESET_SELECTED_SHIPPING_OFFER_DATA_SUCCESS,
    RESET_REGISTRATION_DATA,
    RESET_PARTS,
    SAVE_BILLING_ADDRESS,
    UPDATE_REACTIVATE_ORDER_STATUS,
    PURCHASE_ON_DEMAND_ORDER_SUCCESS,
    REASON_FOR_NOT_SELECTING_OFFERS,
    REASON_FOR_Z4R_RETURN,
    SAVE_CUSTOMER_COMMENTS_SUCCESS,
    SAVE_CUSTOMER_SERVICE_LOCATION_ADDRESS_SUCCESS,
    SECOND_SR_CREATION,
    SAVE_SOFTWARE_ORDER_DETAILS,
} from './actions';
import {
    _getRegisterInfoData,
    _getRegisterInfoDataSuccess,
    _getJarvisDataSuccess,
    _getRegistrationSuccess,
    resetRegistrationReducer,
} from '../registerInfo/registerReducer';
import { saveZipCodeDataReducer } from './zipCodeReducer';
import { saveSafetyFormDataReducer } from './safetyFormReducer';
import { _getShippingAddressData } from './shippingAddressReducer';
import {
    _getSelectedShippingOfferData,
    _resetSelectedShippingOfferData,
} from './selectedShippingOffersReducer';
import {
    _getDeviceShippingOfferData,
    _getAccessoryShippingOfferData,
} from './shippingOffersReducer';

import INITIAL_STATE from './initialState.json';
import { _saveBillingAddressData } from './billingAddressReducer';
import { saveCustomerServiceLocationAddress } from './customerServiceLocationReducer';

export enum ClaimOrderLineItemAttributes {
    None = 0,
    Repair = 1,
    Fulfillment = 2,
    ServiceContract = 4,
    Purchase = 8,
    Replacement = 16,
    Exchange = 32,
    SendImmediately = 64,
    AdvancedExchange = 128,
    OutOfWarranty = 256,
    PhysicalDelivery = 512,
    DigitalDelivery = 1024,
    InStore = 2048,
    Upgrade = 4096,
    InStorePart = 8192,
}

export enum Progress {
    ZipCode,
    ItemSelection,
    Registration,
    RepairOfferSelection,
    UpdateDeviceNewSerialNumber,
    PlacingOrder,
    AddingShippingAddress,
    AddingBillingAddress,
}

export type OrderStateType = Readonly<typeof INITIAL_STATE>;

export default handleActions(
    {
        [combineActions(
            CHECKOUT_ORDER_SUCCESS,
            PURCHASE_ORDER_SUCCESS
        ).toString()]: (state: any, actions: any) => ({
            ...state,
            confirmation: { ...actions.payload.data },
        }),
        [SAVE_SELECTED_OFFER]: saveSelectedOfferReducer,
        [RESET_SELECTED_OFFER]: resetSelectedOfferReducer,
        [SAVE_USER_DEVICE_AND_ACCESSORY_SELECTION]: saveUserDeviceAndAccessorySelectionReducer,
        [DELETE_SELECTED_PARENT]: deleteSelectedParentReducer,
        [DELETE_SELECTED_ACCESSORY]: deleteSelectedAccessoryReducer,
        [GET_PART_INFO_SUCCESS]: _getPartInfoSuccess,
        [PUT_PART_INFO_SUCCESS]:_putPartInfoSuccess,
        [SAVE_ZIP_CODE_DATA]: saveZipCodeDataReducer,
        [SECOND_SR_CREATION]: secondSRCreationReducer,
        [SAVE_SAFETY_FORM_DATA]: saveSafetyFormDataReducer,
        [GET_REGISTER_INFO_DATA]: _getRegisterInfoData,
        [GET_REGISTER_INFO_DATA_SUCCESS]: _getRegisterInfoDataSuccess,
        [GET_JARVIS_DATA_SUCCESS]: _getJarvisDataSuccess,
        [GET_REGISTRATION_SUCCESS]: _getRegistrationSuccess,
        [UPDATE_CREATE_ORDER_PROGRESS]: updateCreateOrderProgressReducer,
        [GET_REPAIR_OFFER]: saveRepairItemsInformationReducer,
        [SAVE_REPAIR_OFFER]: saveRepairOfferReducer,
        [RESET_ORDER_DATA]: resetOrderDataReducer,
        [GET_SHIPPING_ACCESSORY_OFFER_DATA_SUCCESS]: _getAccessoryShippingOfferData,
        [GET_SHIPPING_ADDRESS_DATA_SUCCESS]: _getShippingAddressData,
        [GET_SHIPPING_DEVICE_OFFER_DATA_SUCCESS]: _getDeviceShippingOfferData,
        [GET_SELECTED_SHIPPING_OFFER_DATA_SUCCESS]: _getSelectedShippingOfferData,
        [RESET_SELECTED_SHIPPING_OFFER_DATA_SUCCESS]: _resetSelectedShippingOfferData,
        [RESET_REGISTRATION_DATA]: resetRegistrationReducer,
        [RESET_PARTS]: resetPartsReducer,
        [SAVE_BILLING_ADDRESS]: _saveBillingAddressData,
        [UPDATE_REACTIVATE_ORDER_STATUS]: updateReactivateOrderStatus,
        [PURCHASE_ON_DEMAND_ORDER_SUCCESS]: saveOnDemandPurchaseData,
        [REASON_FOR_NOT_SELECTING_OFFERS]: reasonForNotSelectingOffersReducer,
        [REASON_FOR_Z4R_RETURN]: reasonForZ4RReturnReducer,
        [SAVE_CUSTOMER_SERVICE_LOCATION_ADDRESS_SUCCESS]: saveCustomerServiceLocationAddress,
        [SAVE_CUSTOMER_COMMENTS_SUCCESS]: saveCustomerComments,
        [SAVE_SOFTWARE_ORDER_DETAILS]: saveSoftwareOrderDetailsReducer,
    },
    INITIAL_STATE
);

function _getPartInfoSuccess(state: any, { payload }: any): OrderStateType {
    if (payload) {
        const id = payload.deviceSerial;
        return {
            ...state,
            parts: {
                ...state.parts,
                [id]: payload.data,
            },
        };
    }
    return INITIAL_STATE;
}

function _putPartInfoSuccess(state: any, { payload }: any): OrderStateType {
    console.log('putPartInfoSuccess', payload);
    if (payload) {
        const id = payload.deviceSerial;
        return {
            ...state,
            parts: {
                ...state.parts,
                [id]: payload.data,
            },
        };
    }
    return INITIAL_STATE;
}

function resetPartsReducer(state: any, { payload }: any): OrderStateType {
    if (payload) {
        const id = payload.deviceSerial;
        if (state.parts[id]) {
            delete state.parts[id];
        }
        return {
            ...state,
            parts: {
                ...state.parts,
            },
        };
    }
    return {
        ...state,
        parts: {},
    };
}

function deleteSelectedParentReducer(
    state: any,
    { payload }: { payload: any }
): OrderStateType {
    if (!payload) {
        return state;
    }

    return {
        ...state,
        selection: {
            ...state.selection,
            parent: null,
        },
    };
}

function deleteSelectedAccessoryReducer(
    state: any,
    { payload }: { payload: any }
): OrderStateType {
    if (!payload) {
        return state;
    }

    return {
        ...state,
        selection: {
            ...state.selection,
            accessory: state.selection.accessory.filter(
                (accessory: any) => accessory.sku !== payload
            ),
        },
    };
}

function saveUserDeviceAndAccessorySelectionReducer(
    state: any,
    { payload }: { payload: { parent: {}; accessory: [{}] } }
): OrderStateType {
    if (payload) {
        return {
            ...state,
            selection: {
                parent: payload.parent,
                accessory: payload.accessory,
            },
        };
    }

    return INITIAL_STATE;
}

function saveRepairItemsInformationReducer(
    state: any,
    { payload }: { payload: any }
) {
    return {
        ...state,
        repairItems: payload.repairDetails,
    };
}

function saveRepairOfferReducer(state: any, { payload }: { payload: any }) {
    const { accessoryAndPartRepairOffer, fullDeviceRepairOffer, softwareRepairOffer } = payload;

    return {
        ...state,
        repairOffer: {
            accessoryAndPartRepairOffer,
            fullDeviceRepairOffer,
            softwareRepairOffer,
        },
    };
}

function saveSelectedOfferReducer(state: any, { payload }: { payload: any }) {
    return {
        ...state,
        selectedRepairOffers: {
            ...state.selectedRepairOffers,
            [payload.symptomCode]: payload.offer,
        },
        isOnSiteRepair: payload.offer.isNBDOnsiteOffer ? payload.offer.isNBDOnsiteOffer : false,
    };
}

function reasonForNotSelectingOffersReducer(
    state: any,
    { payload }: { payload: any }):any {
    return {
        ...state,
        reasonForNotSelectingOffers: payload,
    };
    return INITIAL_STATE;
}

function reasonForZ4RReturnReducer(
    state: any,
    { payload }: { payload: any }): any {
    return {
        ...state,
        reasonForZ4RReturn: payload,
    };
    return INITIAL_STATE;
}

function resetSelectedOfferReducer(state: any, { payload }: { payload: any }) {
    return {
        ...state,
        selectedRepairOffers: {},
    };
}

function updateCreateOrderProgressReducer(
    state: any,
    { payload }: { payload: any }
): OrderStateType {
    if (payload) {
        return {
            ...state,
            currentStage: payload,
        };
    }

    return INITIAL_STATE;
}

function updateReactivateOrderStatus(
    state: any,
    { payload }: { payload: any }
): OrderStateType {
    if (payload) {
        return {
            ...state,
            reactivateOrder: payload,
        };
    }

    return INITIAL_STATE;
}

function saveOnDemandPurchaseData(
    state: any,
    { payload }: { payload: any }
): OrderStateType {
    if (payload) {
        return {
            ...state,
            confirmation: payload.data,
        };
    }

    return INITIAL_STATE;
}


function resetOrderDataReducer() {
    return {
        ...INITIAL_STATE,
    };
}


function saveCustomerComments(
    state: any,
    { payload }: { payload: any }
): OrderStateType {
    if (payload) {
        return {
            ...state,
            customerComments: payload.customerComments,
        };
    }
    return state;
}

function secondSRCreationReducer(
    state: any
): OrderStateType {
    return {
        ...state,
        secondSRCreation: true,
    };
}

function saveSoftwareOrderDetailsReducer(
    state: any,
    { payload }: { payload: any }
): OrderStateType {
    if (payload) {
        return {
            ...state,
            softwareRepairDetails: {
                isSDTRun: payload.sdtToolRan,
                softwareResolutionType: payload.resolutionType,
            },
        };
    }
    return state;
}